import { useState, useRef, useCallback, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { X, Loader2, CloudUpload, Upload, Plus, FileText } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { uploadImagesGated, uploadFilesGated, getStorageErrorMessage, isExpectedUploadError, IMAGE_ACCEPT, IMAGE_OR_VIDEO_ACCEPT, isVideoFile, isVideoUrl, videoTileSrc, type StorageFolder } from "@/lib/storage";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

interface PendingImage {
  localUrl: string;
  file: File;
}

interface ImageUploadProps {
  label?: string;
  hint?: string;
  folder: StorageFolder;
  /** Multi-tenant path scoping (#3059 C1 phase 2) — pass straight from
   * `useAuth().companyId`. Uploads are blocked with an error toast while
   * this is falsy (fail-closed — mirrors the `enabled: !!companyId` guard
   * convention for data hooks). */
  companyId: string | null | undefined;
  entityId?: string;
  value?: string[];
  onChange: (urls: string[]) => void;
  maxFiles?: number;
  className?: string;
  disabled?: boolean;
  accept?: string;
}

const isPdfFile = (file: File) => file.type === "application/pdf";
const isPdfUrl = (url: string) => url.toLowerCase().includes(".pdf");
const isVideoPending = (file: File) => file.type.startsWith("video/");

export function ImageUpload({
  label,
  hint,
  folder,
  companyId,
  entityId,
  value = [],
  onChange,
  maxFiles = 10,
  className,
  disabled = false,
  accept = IMAGE_ACCEPT,
}: ImageUploadProps) {
  const acceptsPdf = accept.includes("application/pdf");
  const acceptsVideo = accept.includes("video/");
  const { t } = useLanguage();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [pendingImages, setPendingImages] = useState<PendingImage[]>([]);

  // Cleanup pending image URLs on unmount
  useEffect(() => {
    return () => {
      pendingImages.forEach(p => URL.revokeObjectURL(p.localUrl));
    };
  }, [pendingImages]);

  const handleFileSelect = useCallback(async (files: FileList | null) => {
    if (!files || files.length === 0 || disabled) return;

    if (!companyId) {
      toast.error(t("خطأ في الجلسة، يرجى إعادة تحميل الصفحة", "Session error, please reload the page"));
      return;
    }

    const fileArray = Array.from(files);
    const remainingSlots = maxFiles - value.length - pendingImages.length;
    
    if (fileArray.length > remainingSlots) {
      toast.error(t(
        `يمكنك رفع ${remainingSlots} ملف فقط`,
        `You can only upload ${remainingSlots} more file(s)`
      ));
      return;
    }

    // Create instant local previews
    const pending = fileArray.map(file => ({
      localUrl: URL.createObjectURL(file),
      file,
    }));
    setPendingImages(prev => [...prev, ...pending]);

    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Simulate progress
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => Math.min(prev + 10, 90));
      }, 100);

      // acceptsVideo/acceptsPdf path uses uploadFilesGated (routes images/videos
      // to S3 for property/unit folders when USE_S3_PROPERTY_IMAGES is enabled,
      // PDFs always go to Supabase Storage). Pure-image path uses uploadImagesGated
      // which routes property/unit uploads to S3; all other folders use Supabase.
      const results = acceptsVideo || acceptsPdf
        ? await uploadFilesGated(fileArray, folder, companyId, entityId)
        : await uploadImagesGated(fileArray, folder, companyId, entityId);
      
      clearInterval(progressInterval);
      setUploadProgress(100);

      // Get new URLs
      const newUrls = results.map(r => r.url);
      
      // Replace pending with actual URLs
      onChange([...value, ...newUrls]);
      
      // Cleanup local URLs
      pending.forEach(p => URL.revokeObjectURL(p.localUrl));
      setPendingImages(prev => prev.filter(p => !pending.includes(p)));
      
      toast.success(t("تم رفع الصور بنجاح", "Images uploaded successfully"));
    } catch (error: unknown) {
      // Remove failed pending images and cleanup
      pending.forEach(p => URL.revokeObjectURL(p.localUrl));
      setPendingImages(prev => prev.filter(p => !pending.includes(p)));
      // User-input validation errors (wrong MIME type, oversized file, duplicate)
      // are expected — skip console.error so Sentry's captureConsoleIntegration
      // doesn't forward them as real application errors.
      if (!isExpectedUploadError(error)) console.error("Image upload error:", error);
      // Resolve the StorageError code to a bilingual message in the storage
      // module, then localize via t(). Falls back to "Image upload failed"
      // for non-StorageError throws.
      const msg = getStorageErrorMessage(error, {
        ar: "فشل رفع الصور",
        en: "Image upload failed",
      });
      toast.error(t(msg.ar, msg.en), { duration: 8000 });
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  }, [value, onChange, folder, companyId, entityId, maxFiles, t, disabled, pendingImages, acceptsPdf, acceptsVideo]);

  const handleRemove = (index: number) => {
    const newUrls = value.filter((_, i) => i !== index);
    onChange(newUrls);
  };

  const handleRemovePending = (index: number) => {
    const pending = pendingImages[index];
    if (pending) {
      URL.revokeObjectURL(pending.localUrl);
    }
    setPendingImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled) setIsDragging(true);
  }, [disabled]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (!disabled) handleFileSelect(e.dataTransfer.files);
  }, [handleFileSelect, disabled]);

  const totalImages = value.length + pendingImages.length;

  return (
    <div className={cn("space-y-2", className)}>
      {label && <Label className="text-sm font-medium">{label}</Label>}
      
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={accept}
        onChange={(e) => handleFileSelect(e.target.files)}
        className="hidden"
        disabled={disabled}
      />

      {/* Upload Progress */}
      {isUploading && (
        <div className="p-4 bg-muted/50 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Loader2 className="h-4 w-4 animate-spin text-primary" />
            <span className="text-sm">{t("جاري رفع الصور...", "Uploading images...")}</span>
          </div>
          <Progress value={uploadProgress} className="h-2" />
        </div>
      )}

      {/* Existing Images + Pending Images */}
      {totalImages > 0 && (
        <div className="grid grid-cols-4 gap-2 mb-3">
          {/* Uploaded images / PDFs */}
          {value.map((url, index) => (
            <div key={`uploaded-${index}`} className="relative aspect-square rounded-lg overflow-hidden border group">
              {isPdfUrl(url) ? (
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full h-full flex flex-col items-center justify-center gap-1 bg-muted hover:bg-muted/80 transition-colors"
                >
                  <FileText className="h-8 w-8 text-destructive" />
                  <span className="text-xs text-muted-foreground">PDF</span>
                </a>
              ) : isVideoUrl(url) ? (
                <video
                  src={videoTileSrc(url)}
                  className="w-full h-full object-cover"
                  muted
                  playsInline
                  preload="metadata"
                />
              ) : (
                <img src={url} alt="" className="w-full h-full object-cover" />
              )}
              {!disabled && (
                <button
                  type="button"
                  onClick={() => handleRemove(index)}
                  className="absolute top-1 end-1 p-1 bg-destructive text-destructive-foreground rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-destructive/90"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
              {index === 0 && (
                <span className="absolute bottom-1 start-1 px-2 py-0.5 bg-primary text-primary-foreground text-xs rounded">
                  {t("رئيسية", "Main")}
                </span>
              )}
              {isVideoUrl(url) && (
                <span className="absolute bottom-1 start-1 px-2 py-0.5 bg-primary/90 text-primary-foreground text-xs rounded">
                  {t("فيديو", "Video")}
                </span>
              )}
            </div>
          ))}

          {/* Pending images / PDFs (uploading) */}
          {pendingImages.map((pending, index) => (
            <div key={`pending-${index}`} className="relative aspect-square rounded-lg overflow-hidden border group">
              {isVideoPending(pending.file) ? (
                <video
                  src={pending.localUrl}
                  className="w-full h-full object-cover opacity-60"
                  muted
                  playsInline
                  preload="metadata"
                />
              ) : isPdfFile(pending.file) ? (
                <div className="w-full h-full flex flex-col items-center justify-center gap-1 bg-muted opacity-60">
                  <FileText className="h-8 w-8 text-destructive" />
                  <span className="text-xs text-muted-foreground">PDF</span>
                </div>
              ) : (
                <img src={pending.localUrl} alt="" className="w-full h-full object-cover opacity-60" />
              )}
              <div className="absolute inset-0 flex items-center justify-center bg-background/50">
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
              </div>
              {!disabled && !isUploading && (
                <button
                  type="button"
                  onClick={() => handleRemovePending(index)}
                  className="absolute top-1 end-1 p-1 bg-destructive text-destructive-foreground rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-destructive/90"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
          ))}

          {/* Add More Tile */}
          {totalImages < maxFiles && !isUploading && !disabled && (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="aspect-square rounded-lg border-2 border-dashed border-muted-foreground/25 
                         flex flex-col items-center justify-center gap-1 
                         hover:border-primary/50 hover:bg-primary/5 transition-colors cursor-pointer"
            >
              <Plus className="h-6 w-6 text-muted-foreground/50" />
              <span className="text-xs text-muted-foreground/70">
                {t("إضافة", "Add")}
              </span>
            </button>
          )}
        </div>
      )}

      {/* Drop Zone - Only show in empty state */}
      {totalImages === 0 && !isUploading && (
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => !disabled && fileInputRef.current?.click()}
          className={cn(
            "border-2 border-dashed rounded-xl p-8 transition-colors min-h-[200px]",
            "flex flex-col items-center justify-center text-center gap-3",
            isDragging ? "border-primary bg-primary/5" : "border-muted-foreground/25 hover:border-primary/50",
            disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
          )}
        >
          <CloudUpload className="h-10 w-10 text-muted-foreground/50" />
          <div className="space-y-1">
            <p className="text-sm font-medium text-muted-foreground">
              {hint || t("اسحب وأفلت الصور هنا أو انقر للرفع", "Drag and drop images here or click to upload")}
            </p>
            <p className="text-xs text-muted-foreground/70">
              {acceptsVideo
                ? t(
                    "PNG, JPG, GIF، MP4 أو MOV (حد أقصى 10MB للصور، 200MB للفيديو)",
                    "PNG, JPG, GIF, MP4 or MOV (max 10MB for images, 200MB for video)",
                  )
                : acceptsPdf
                ? t("PNG, JPG, GIF أو PDF (حد أقصى 10MB)", "PNG, JPG, GIF or PDF (max 10MB each)")
                : t("PNG, JPG أو GIF (حد أقصى 10MB)", "PNG, JPG or GIF (max 10MB each)")}
            </p>
          </div>
          <Button 
            type="button" 
            variant="default" 
            size="sm"
            disabled={disabled}
          >
            <Upload className="h-4 w-4 me-2" />
            {t("رفع", "Upload")}
          </Button>
        </div>
      )}
    </div>
  );
}
